import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import useOAuthPopup from './useOAuthPopup';
import { getOAuthProvider } from '../oauthProviderRegistry';
import { SAVE_PARTNER_ACCOUNT, GET_PARTNER_ACCOUNTS } from '../queries';
import { csrftoken } from '../../services/api';

/**
 * Initiates OAuth flow by requesting auth URL from backend.
 *
 * @param {string} authEndpoint - The OAuth authorization endpoint URL
 * @param {number} partnerId - The partner ID to authenticate
 * @returns {Promise<string>} The OAuth URL to redirect to
 */
const initiateOAuth = async (authEndpoint, partnerId) => {
    const response = await fetch(authEndpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({
            state: window.btoa(JSON.stringify([partnerId, null])),
            origin: 'partner_configuration',
            sdk_token: true,
        }),
    });

    const data = await response.json();
    if (data.status !== 0) {
        throw new Error(data.message || 'Failed to initiate OAuth');
    }

    return data.value.url;
};

/**
 * Hook for complete OAuth flow.
 * Manages popup, callback handling, and account saving.
 *
 * @param {string} partnerId - The partner ID
 * @param {string} partnerName - The partner name (used for OAuth provider lookup)
 * @param {string} partnerDisplayName - The partner display name (used for error messages)
 * @returns {Object} - { startOAuthFlow, error, clearError }
 */
export default function useOAuthFlow(partnerId, partnerName, partnerDisplayName) {
    const [error, setError] = useState(null);

    const { openPopup, redirectPopup, closePopup, registerCallback } = useOAuthPopup();

    const [savePartnerAccount] = useMutation(SAVE_PARTNER_ACCOUNT, {
        refetchQueries: [{ query: GET_PARTNER_ACCOUNTS, variables: { partnerId } }],
    });

    const startOAuthFlow = useCallback(async () => {
        const provider = getOAuthProvider(partnerName);
        if (!provider) {
            setError(`OAuth not supported for ${partnerDisplayName}`);
            return;
        }

        setError(null);

        try {
            // 1. Open popup
            openPopup(provider.displayName);

            // 2. Register callback handler before redirect
            registerCallback(async (accessToken, errorMessage) => {
                if (errorMessage) {
                    setError(errorMessage);
                    return;
                }

                if (!accessToken) {
                    setError('Authentication failed - no token received');
                    return;
                }

                try {
                    // 3. Extract credentials using provider-specific logic
                    const { loginId, loginData } = provider.extractCredentials(accessToken);

                    // 4. Save account using GraphQL mutation
                    const result = await savePartnerAccount({
                        variables: {
                            partnerId,
                            loginId,
                            loginData,
                            loginAccounts: [],
                        },
                    });

                    if (result.data?.savePartnerAccount?.error) {
                        setError(result.data.savePartnerAccount.error);
                    }
                } catch (saveError) {
                    setError(saveError.message);
                }
            });

            // 3. Get OAuth URL from backend
            const oauthUrl = await initiateOAuth(provider.authEndpoint, partnerId);

            // 4. Redirect popup to OAuth provider
            redirectPopup(oauthUrl);
        } catch (err) {
            closePopup();
            setError(err.message);
        }
    }, [
        partnerId,
        partnerName,
        partnerDisplayName,
        openPopup,
        redirectPopup,
        closePopup,
        registerCallback,
        savePartnerAccount,
    ]);

    const clearError = useCallback(() => setError(null), []);

    return {
        startOAuthFlow,
        error,
        clearError,
    };
}
